import { useForm } from '@magicdoorinc/form';
import { createMutation } from '@magicdoorinc/ui';
import { createSignal, onMount, createResource } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useBankAccountsList } from '~/contexts/bank-accounts';
import { createMagicDoorContext } from '~/contexts/utils';
import { mode, PaymentMode } from '~/hooks/usePaymentMode';
import { bankAccountRepository } from '~/repositories/bankAccountRepository';
import { companyRepository } from '~/repositories/companiesRepository';
import type { CreateBankAccountDto, ResolveSelfManagedDto } from '~/swagger/portal/Api';

export type CompanyType = 'owner-manager' | 'property-management-company';

export enum CompanyTypeConfirmStep {
  SelectCompanyType = 'SelectCompanyType',
  SelectCompanyBankAccounts = 'SelectCompanyBankAccounts',
  CreateCompanyBankAccount = 'CreateCompanyBankAccount',
  SelectDefaultCompanyBankAccount = 'SelectDefaultCompanyBankAccount',
  Confirmation = 'Confirmation',
}

export const [CompanyTypeConfirmProvider, useCompanyTypeConfirm] = createMagicDoorContext('CompanyTypeConfirm', () => {
  const [step, setStep] = createSignal<CompanyTypeConfirmStep>();
  const [visible, setVisible] = createSignal<boolean>(false);
  const { mutateBankAccounts } = useBankAccountsList();
  const [resolveSelfManagedStore, setResolveSelfManagedStore] = createStore<
    Omit<ResolveSelfManagedDto, 'selfManaged'> & { selfManaged?: boolean } & { propertyBankAccountIds?: string[] }
  >({
    selfManaged: undefined,
  });
  const [unassignedBankAccounts] = createResource(
    () => visible() && step() === CompanyTypeConfirmStep.SelectCompanyBankAccounts,
    () => companyRepository.getUnassignedBankAccounts()
  );
  const handleCloseModal = () => {
    setVisible(false);
  };

  const createNewBankAccountForm = useForm();
  const selectDefaultBankAccountForm = useForm();

  const handleSelectCompanyType = async () => {
    if (!resolveSelfManagedStore.selfManaged) {
      setStep(CompanyTypeConfirmStep.SelectCompanyBankAccounts);
    } else {
      setStep(CompanyTypeConfirmStep.Confirmation);
    }
  };

  const handleSelectBankAccounts = async () => {
    const companyBankAccountIds = resolveSelfManagedStore.companyBankAccountIds;
    if (!companyBankAccountIds) return;

    if (companyBankAccountIds.length === 1) {
      setResolveSelfManagedStore({
        defaultCompanyBankAccountId: companyBankAccountIds[0],
        companyBankAccountIds: undefined,
      });
      handleConfirmationConfirm();
    } else {
      setResolveSelfManagedStore({
        defaultCompanyBankAccountId: undefined,
      });
      setStep(CompanyTypeConfirmStep.SelectDefaultCompanyBankAccount);
    }
  };

  const handleSelectDefaultNext = async () => {
    const { validateStatus } = selectDefaultBankAccountForm.validateForm();
    if (!validateStatus) return;

    const { defaultCompanyBankAccountId } = selectDefaultBankAccountForm.formStore;
    setResolveSelfManagedStore({
      defaultCompanyBankAccountId,
    });

    handleConfirmationConfirm();
  };

  const handleCreateNewBankAccount = createMutation(async () => {
    const { validateStatus } = createNewBankAccountForm.validateForm();
    if (!validateStatus) return;

    const formStore = createNewBankAccountForm.formStore as CreateBankAccountDto;
    const newAccount = await bankAccountRepository.createBankAccount({
      ...formStore,
      scheduledSweepDays: mode() === PaymentMode.Pay ? [] : (formStore.scheduledSweepDays ?? []),
      companyAccount: true,
    });

    mutateBankAccounts((prev) => [newAccount, ...(prev ?? [])]);

    setStep(CompanyTypeConfirmStep.SelectCompanyBankAccounts);
  });

  const handleConfirmationConfirm = createMutation(async () => {
    const bankAccountId = resolveSelfManagedStore.defaultCompanyBankAccountId;
    const companyBankAccountIds = resolveSelfManagedStore.companyBankAccountIds;
    const isSelfManaged = resolveSelfManagedStore.selfManaged;
    if (isSelfManaged === undefined) {
      return;
    }

    await companyRepository.updateCompanySelfManaged({
      selfManaged: isSelfManaged,
      defaultCompanyBankAccountId: bankAccountId,
      companyBankAccountIds: companyBankAccountIds,
    });

    window.location.href = '/';
  });

  const handleCompanyTypeChange = (selfManaged: boolean) => {
    setResolveSelfManagedStore({
      selfManaged: selfManaged,
      defaultCompanyBankAccountId: undefined,
      companyBankAccountIds: undefined,
    });
  };

  onMount(async () => {
    const result = await companyRepository.checkCompanySelfManaged();
    if (result.needChoose) {
      setVisible(true);
      setStep(CompanyTypeConfirmStep.SelectCompanyType);
    }
  });

  return {
    step,
    setStep,
    visible,
    createNewBankAccountForm,
    selectDefaultBankAccountForm,
    handleCompanyTypeChange,
    handleCloseModal,
    handleSelectCompanyType,
    handleSelectBankAccounts,
    handleSelectDefaultNext,
    handleCreateNewBankAccount,
    handleConfirmationConfirm,
    resolveSelfManagedStore,
    setResolveSelfManagedStore,
    unassignedBankAccounts,
  };
});
